import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Cpu, Send, Loader2, X, Sparkles, Mic, MicOff, ChevronDown,
  Zap, CheckCircle2, AlertCircle, ExternalLink, Bot,
} from 'lucide-react';

const SUGGESTIONS = [
  "Quelles sont les tâches en cours ?",
  "Relance tous les devis",
  "Envoie un rappel aux sous-traitants avec des documents expirés",
  "Combien de contrats en attente de signature ?",
];

/**
 * Floating conversational copilot — integrated into the Dashboard.
 * Connects to /api/orchestrator/copilot which uses GPT-5.2 to interpret the
 * user's natural-language command, execute the right orchestrator action,
 * and reply in French.
 */
export default function CopilotChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "👋 Salut ! Je suis l'agent Orchestrateur. Dis-moi ce que tu veux faire (relancer un devis, vérifier les documents, lister les tâches…) ou clique sur une suggestion ci-dessous." },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    if (open && !minimized && inputRef.current) inputRef.current.focus();
  }, [open, minimized]);

  const send = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || sending) return;
    setInput('');
    const history = messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setSending(true);
    try {
      const r = await api.post('/orchestrator/copilot', { message: trimmed, history });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: r.data.reply,
        intent: r.data.intent,
        action_result: r.data.action_result,
      }]);
      // Auto-navigate when the AI decides
      if (r.data.action_result?.action === 'navigate' && r.data.action_result.link) {
        setTimeout(() => navigate(r.data.action_result.link), 600);
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, je n'arrive pas à te répondre. Réessaie dans un instant.",
        error: true,
      }]);
    }
    setSending(false);
  }, [input, messages, sending, navigate]);

  // ===== Voice input (Web Speech API) =====
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("La saisie vocale n'est pas supportée par ce navigateur.");
      return;
    }
    if (recording && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'fr-FR';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (ev) => {
      let txt = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        txt += ev.results[i][0].transcript;
      }
      setInput(txt);
    };
    rec.onend = () => { setRecording(false); recognitionRef.current = null; };
    rec.onerror = () => { setRecording(false); recognitionRef.current = null; };
    recognitionRef.current = rec;
    setRecording(true);
    rec.start();
  };

  // ===== Floating bubble (closed state) =====
  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setMinimized(false); }}
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-gradient-to-br from-blue-900 via-indigo-800 to-blue-900 text-white shadow-2xl shadow-blue-900/30 hover:scale-105 hover:shadow-blue-900/50 transition-all"
        data-testid="copilot-open-btn"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-amber-400 rounded-full blur-md opacity-60 group-hover:opacity-80 animate-pulse" />
          <div className="relative w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-2 ring-white/20">
            <Cpu className="w-5 h-5" />
          </div>
        </div>
        <div className="flex flex-col items-start">
          <span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Copilote IA</span>
          <span className="text-sm font-bold leading-tight">Demande-moi tout</span>
        </div>
        <Sparkles className="w-4 h-4 text-amber-300 ml-1" />
      </button>
    );
  }

  return (
    <div
      className={`fixed z-50 transition-all duration-300 ${
        minimized
          ? 'bottom-6 right-6 w-72 h-14'
          : 'bottom-6 right-6 w-[min(420px,calc(100vw-32px))] h-[min(640px,calc(100vh-100px))]'
      }`}
      data-testid="copilot-panel"
    >
      <div className="h-full flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/30 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-blue-900 via-indigo-800 to-blue-900 text-white cursor-pointer"
          onClick={() => minimized && setMinimized(false)}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-amber-400 rounded-full blur-md opacity-50 animate-pulse" />
            <div className="relative w-8 h-8 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
              <Cpu className="w-4 h-4" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm leading-tight">Agent Orchestrateur</p>
            <p className="text-[11px] text-blue-200 leading-tight">
              {sending ? 'Réflexion…' : 'En ligne · GPT-5.2'}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
            className="p-1.5 hover:bg-white/10 rounded-lg"
            data-testid="copilot-minimize-btn"
            title={minimized ? 'Agrandir' : 'Réduire'}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${minimized ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="p-1.5 hover:bg-white/10 rounded-lg"
            data-testid="copilot-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!minimized && (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} onNavigate={navigate} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-slate-500 px-2">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                    <Cpu className="w-3.5 h-3.5 text-blue-700" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions */}
            {messages.length <= 2 && !sending && (
              <div className="px-3 py-2 border-t border-slate-100 bg-white">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 px-1">Suggestions</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-blue-100 hover:text-blue-700 text-slate-700 transition-colors"
                      data-testid={`copilot-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-slate-100 p-3 bg-white">
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault(); send();
                      }
                    }}
                    placeholder="Pose ta question ou donne un ordre…"
                    rows={1}
                    className="w-full resize-none px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400"
                    style={{ maxHeight: '120px' }}
                    data-testid="copilot-input"
                  />
                  <button
                    type="button"
                    onClick={toggleVoice}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${
                      recording
                        ? 'bg-red-100 text-red-600 animate-pulse'
                        : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                    title={recording ? 'Arrêter la dictée' : 'Dictée vocale'}
                    data-testid="copilot-voice-btn"
                  >
                    {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="p-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:cursor-not-allowed text-white transition-colors"
                  data-testid="copilot-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
              <p className="text-[10px] text-slate-400 mt-1.5 text-center">
                <Sparkles className="inline w-3 h-3 mr-0.5 text-amber-500" />
                L'agent peut agir — relances, rappels, navigation.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Individual message bubble with optional action chip from the orchestrator. */
function MessageBubble({ msg, onNavigate }) {
  const isUser = msg.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-md bg-blue-600 text-white text-sm whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    );
  }

  const action = msg.action_result;
  return (
    <div className="flex gap-2">
      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
        <Cpu className="w-3.5 h-3.5 text-blue-700" />
      </div>
      <div className="flex-1 max-w-[85%] space-y-1.5">
        <div className={`px-3 py-2 rounded-2xl rounded-tl-md text-sm whitespace-pre-wrap ${
          msg.error
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-white text-slate-800 border border-slate-200'
        }`}>
          {msg.content}
        </div>

        {/* Action chip */}
        {action?.action === 'run_all_auto' && action.summary && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
            <Zap className="w-3 h-3" />
            {action.summary.ok} action(s) exécutée(s)
            {action.summary.ko > 0 && ` · ${action.summary.ko} échec(s)`}
          </div>
        )}
        {action?.action === 'send_relance' && action.success && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
            <CheckCircle2 className="w-3 h-3" /> Relance envoyée
          </div>
        )}
        {action?.action === 'send_relance' && action.error && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
            <AlertCircle className="w-3 h-3" /> {action.error}
          </div>
        )}
        {action?.action === 'send_doc_reminder' && (action.sent != null) && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
            <CheckCircle2 className="w-3 h-3" /> {action.sent} rappel(s) envoyé(s)
          </div>
        )}
        {action?.action === 'list_tasks' && action.tasks?.length > 0 && (
          <div className="space-y-1">
            {action.tasks.slice(0, 5).map((t, i) => (
              <button
                key={i}
                onClick={() => onNavigate(t.link)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-xs text-left transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${
                  t.severity === 'critical' ? 'bg-red-500' :
                  t.severity === 'warning' ? 'bg-amber-500' : 'bg-slate-400'
                }`} />
                <span className="flex-1 truncate text-slate-800">{t.title}</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </button>
            ))}
            {action.tasks.length > 5 && (
              <p className="text-[11px] text-slate-400 px-2">+ {action.tasks.length - 5} autre(s)</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
